import { useMatches } from '@remix-run/react'
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react'
import { useNonce } from '~/utils/nonce-provider.ts'

declare global {
	const grecaptcha: {
		enterprise: {
			ready: (cb: () => void) => void
			execute: (siteKey: string, options: { action: string }) => Promise<string>
		}
	}
}

interface Props {
	siteKey: string
	children: React.ReactNode
}

type RecaptchaStatus = 'loading' | 'ready' | 'failed'

const LOAD_TIMEOUT_MS = 6000

export const RECAPTCHA_ROUTE_PREFIXES = [
	'routes/_auth+/',
	'routes/reverse-recruiter_+/',
]

function useRecaptchaNeeded() {
	const matches = useMatches()
	return matches.some(match =>
		RECAPTCHA_ROUTE_PREFIXES.some(prefix => match.id.startsWith(prefix)),
	)
}

const providerContext = createContext<{
	token: string | null
	siteKey: string
	setToken: (token: string) => void
	status: RecaptchaStatus
}>({
	token: null,
	siteKey: '',
	setToken: () => {},
	status: 'loading',
})

function useRecaptchaScript(
	src: string,
	nonce: string,
	siteKey: string,
	enabled: boolean,
) {
	const [status, setStatus] = useState<RecaptchaStatus>('loading')

	useEffect(() => {
		if (siteKey === 'test-key') {
			setStatus('ready')
			return
		}
		if (!enabled) return

		let cancelled = false
		let generation = 0
		let timer: ReturnType<typeof setTimeout> | undefined

		const attempt = (n: number) => {
			if (cancelled) return
			const mine = ++generation

			if (typeof grecaptcha !== 'undefined') {
				setStatus('ready')
				return
			}

			const script = document.createElement('script')
			script.src = n === 0 ? src : `${src}&r=${n}`
			script.nonce = nonce
			script.async = true
			script.defer = true

			const settle = (next: RecaptchaStatus) => {
				if (cancelled || mine !== generation) return
				clearTimeout(timer)
				if (next === 'ready') {
					setStatus('ready')
					return
				}
				script.remove()
				if (n === 0) attempt(1)
				else setStatus('failed')
			}

			script.onload = () => settle('ready')
			script.onerror = () => settle('failed')
			timer = setTimeout(() => settle('failed'), LOAD_TIMEOUT_MS)

			document.head.appendChild(script)
		}

		const start = () => attempt(0)

		if (
			document.readyState === 'interactive' ||
			document.readyState === 'complete'
		) {
			start()
			return () => {
				cancelled = true
				clearTimeout(timer)
			}
		}

		const onReadyStateChange = () => {
			if (document.readyState === 'interactive') {
				document.removeEventListener('readystatechange', onReadyStateChange)
				start()
			}
		}
		document.addEventListener('readystatechange', onReadyStateChange)

		return () => {
			cancelled = true
			clearTimeout(timer)
			document.removeEventListener('readystatechange', onReadyStateChange)
		}
	}, [src, nonce, siteKey, enabled])

	return status
}

export const useRecaptcha = (action: string) => {
	const { token, siteKey, setToken, status } = useContext(providerContext)
	useEffect(() => {
		if (siteKey === 'test-key') {
			setToken('test-token')
			return
		}

		if (status === 'ready' && typeof grecaptcha !== 'undefined') {
			grecaptcha.enterprise.ready(async () => {
				const token = await grecaptcha.enterprise.execute(siteKey, {
					action: action,
				})
				setToken(token)
			})
		}
	}, [siteKey, action, setToken, status])
	return { token, status }
}

export const useRecaptchaExecutor = () => {
	const { siteKey, status } = useContext(providerContext)
	const settled = siteKey === 'test-key' || status !== 'loading'

	const execute = useCallback(
		async (action: string): Promise<string | null> => {
			if (siteKey === 'test-key') return 'test-token'
			if (typeof grecaptcha === 'undefined') return null
			try {
				return await new Promise<string>((resolve, reject) => {
					grecaptcha.enterprise.ready(() => {
						grecaptcha.enterprise
							.execute(siteKey, { action })
							.then(resolve, reject)
					})
				})
			} catch {
				return null
			}
		},
		[siteKey],
	)

	return { status, settled, execute }
}

export function RecaptchaProvider({ siteKey, children }: Props) {
	const [token, setToken] = useState<string | null>(null)
	const nonce = useNonce()
	const needed = useRecaptchaNeeded()

	const scriptSrc = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}&badge=bottomleft`
	const scriptStatus = useRecaptchaScript(scriptSrc, nonce, siteKey, needed)

	const status: RecaptchaStatus =
		needed || siteKey === 'test-key' ? scriptStatus : 'failed'

	return (
		<providerContext.Provider value={{ token, siteKey, setToken, status }}>
			{children}
		</providerContext.Provider>
	)
}
